/** @file Types used in merchant data */

import type {
  AgentName,
  CheckoutMode,
  ConfidencePosture,
  EscalationInstruction,
  FormalityLevel,
  OperatingHours,
  PaylinkCustomerInfo,
  ProductDisplay,
  ProactivenessLevel,
  SentenceStyle,
  SuggestedPromptLabel,
  ToneProfile,
  UpsellPosture,
} from 'skipify-types';
import { OrderByValue, SortQuery } from '../paylink/types';
import { CardBrand } from '../../../util/constants';

export type BrandingValue = {
  displayName?: string;
  logoSrc?: string;
  color?: string;
};

export type CoBrandingValue = {
  logo?: string;
};

export type Domain = {
  /** @example "https://checkout-dev.gocartpay.com" */
  url: string;
  isInherited: boolean;
  id: string;
};

export type ConfigResponse<T = JSONValue> = {
  configId: string;
  merchantId: string;
  name: string;
  value: T;
  type?: string;
  enabled: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  isInherited?: boolean;
};

export type CreatePaylinkCustomerBody = Partial<PaylinkCustomerInfo> & {
  merchantId: string;
};

export type GetMerchantsByUserParams = {
  pageNumber?: number;
  pageSize?: number;
  orderBy?: 'asc' | 'desc';
  sortBy?: string;
  maxDepth?: number;
  merchantName?: string;
  merchantId?: string;
  onlyParentMerchants?: boolean;
  onlyChildMerchants?: boolean;
};

export type GetMerchantsByUserQuery = {
  userId: string;
  token: string;
  params?: GetMerchantsByUserParams;
};

/** @deprecated Use GetMerchantsByUserParams — paginated endpoint merged into GET .../merchants */
export type GetPaginatedMerchantsByUserParams = GetMerchantsByUserParams;

/** @deprecated Use GetMerchantsByUserQuery */
export type GetPaginatedMerchantsByUserQuery = GetMerchantsByUserQuery;

export const paylinkCustomerSortableFields = ['firstName', 'lastName', 'email'] as const;
export type PaylinkCustomerSortableField = (typeof paylinkCustomerSortableFields)[number];
export type SearchPaylinkCustomersSortQuery = SortQuery<PaylinkCustomerSortableField>;

export type SearchPaylinkCustomersQueryFilters = {
  customerId?: string[];
  cardOnFile?: boolean;
};

export type SearchPaylinkCustomersQuery = SearchPaylinkCustomersSortQuery &
  SearchPaylinkCustomersQueryFilters & {
    merchantId: string;
    searchQuery?: string;
    pageSize?: number;
    pageNumber?: number;
  };

export const auditLogResource = [
  'configs',
  'callbacks',
  'whitelists',
  'credentials',
  'urls',
  'webhooks',
  'agenticSettings',
  'portalUserAccess',
] as const;
export type AuditLogResource = (typeof auditLogResource)[number];

export type AuditLogAction = 'create' | 'update' | 'delete';

export type AuditLogEntry = {
  auditLogId: string;
  merchantId: string;
  action: AuditLogAction;
  resource: AuditLogResource;
  previousValue: JSONValue | null;
  newValue: JSONValue | null;
  timestamp: Date;
  user: string;
};

export type GetAuditLogSortQuery = {
  orderBy?: OrderByValue;
};

export type GetAuditLogsQueryFilters = {
  resources?: AuditLogResource[];
  createdBefore?: string;
  createdAfter?: string;
};

export type GetAuditLogsQuery = GetAuditLogsQueryFilters &
  GetAuditLogSortQuery & {
    merchantId: string;
    pageSize?: number;
    pageNumber?: number;
  };

type PublicFeeConfig = {
  enabled: boolean;
  description: string;
};

/** SDK-safe agentic settings on public GET. Omitted unless `agents.enabled` is true. */
export type PublicAgenticSettings = {
  Ella?: {
    personalities?: Array<{
      personalityId: string;
      enabled: boolean;
      displayName: string;
      avatar: { src: string };
    }>;
    productExperience?: { widgetPosition?: { right: number; bottom: number } };
    checkoutMode?: { mode: CheckoutMode; handoffUrl?: string };
  };
};

export type GetPublicMerchantResponse = {
  merchantId: string;
  topLevelMerchantId?: string;
  parentMerchantName?: string;
  branding: BrandingValue;
  cobranding: CoBrandingValue;
  urls: string[];
  checkoutTestMode: boolean;
  industry?: string;
  noShippingRequired: boolean;
  feeConfigs: {
    GENERAL_FEE: PublicFeeConfig;
    SURCHARGE_FEE: PublicFeeConfig;
  };
  cartConfigs: {
    DISALLOW_CART_EDITS: { enabled: boolean };
  };
  acceptedCurrency: AcceptedCurrency;
  paylinksRecurringCheckbox: boolean;
  agenticSettings?: PublicAgenticSettings;
};

export type UploadPaylinkCustomersBody = {
  file: File;
  shouldOverwrite: boolean;
};

export type UploadPaylinkCustomersResponse = {
  file: string;
  totalUploaded: number;
  totalErrors: number;
  totalDuplicates: number;
};

export type JSONValue =
  string | number | boolean | { [x: string]: JSONValue | undefined } | Array<JSONValue>;

export type SplitPaymentValue = {
  maxPayments: number;
};

export type PartialPaymentValue = {
  enabled: boolean;
};

export type SupportedCountriesValue = {
  supportAll: boolean;
  countries?: string[];
};

export type ShopperCredentialSharingValue = {
  panSharingEnabled: boolean;
  networkTokenSharingEnabled: boolean;
};

export type AdditionalTransactionDataValue = {
  levelTwo: boolean;
  levelThree: boolean;
};

type BaseFeeValue = {
  description: string;
  feeType: 'GENERAL_FEE' | 'SURCHARGE_FEE';
};

type FlatFeeValue = {
  value: number;
  currencyCode: string;
  style: 'FLAT';
} & BaseFeeValue;

type PercentageFeeValue = {
  value: number;
  style: 'PERCENTAGE';
} & BaseFeeValue;

type ExternalFeeValue = {
  credentialType: 'payment';
  style: 'EXTERNAL';
} & BaseFeeValue;

export type FeeValue = FlatFeeValue | PercentageFeeValue | ExternalFeeValue;

/**
 * Type guard to access feevalue.value without error.
 */
export function isFeeWithValue(
  fee: Partial<FeeValue>,
): fee is Partial<FeeValue> & { value: number } {
  return fee.style !== undefined && fee.style !== 'EXTERNAL';
}

export type TermLink = {
  url: string;
  enabled: boolean;
  complianceStatus: 'PENDING' | 'REJECTED' | 'APPROVED';
  comment?: string;
};

export type TermLinksValue = {
  privacyPolicy?: TermLink;
  termsOfUse?: TermLink;
  termsAndConditions?: TermLink;
};

export type SettlementTypeValue = 'AuthCapture' | 'Sale';

export type AgenticCheckoutValue = {
  maxTransactionAmount?: number;
  maxLookupShopperCalls?: number;
  maxGeneratePaylinkCalls?: number;
};

/** ELLA merchant config — merchant-authored rules applied by the Ella assistant. */
export type EllaRule = {
  name?: string;
  text: string;
  enabled: boolean;
};

/** Merchant agentic settings (MMS `/merchants/{id}/agentic-settings`). */
export type AgenticPronunciationEntry = { text: string; pronunciation: string };

/** Escalation settings — threshold, hours, and instruction are required; contacts are optional. */
export type AgenticEscalationSettings = {
  threshold: number;
  operatingHours: OperatingHours;
  instruction: EscalationInstruction;
  contactEmail?: string;
  contactPhoneNumber?: string;
  contactUrl?: string;
};

/** Support module settings — Ella KB answers and helpdesk tickets on the storefront widget. */
export type AgenticSupportSettings = {
  enabled: boolean;
};

/** Help-center answers without the support module; support always includes them. */
export type AgenticKnowledgeBaseSettings = {
  enabled: boolean;
};

export type AgenticPromptManagement = {
  displayName?: string;
  tone?: ToneProfile;
  toneDescription?: string;
  formality: FormalityLevel;
  sentenceStyle: SentenceStyle;
  sentenceStyleDescription: string;
  customGreeting?: string;
  greetingMessage?: string;
  brandVocabulary: {
    mirrorSiteTerminology: boolean;
    terminologyDescription: string;
  };
  suggestedPrompts: SuggestedPromptLabel[];
};

/** Per-agent identity (name, avatar, tone, voice) stored on Ella agentic settings. */
export type AgenticPersonalityAvatar = {
  src: string;
};

export type AgenticPersonalityVoice = {
  profileId: string;
};

export type AgenticPersonality = {
  personalityId: string;
  enabled: boolean;
  displayName: string;
  avatar: AgenticPersonalityAvatar;
  tone: ToneProfile;
  voice: AgenticPersonalityVoice;
  toneDescription?: string;
  isDeleted?: boolean;
};

/** Writable personality accepted by PATCH when creating a new agent. */
export type AgenticPersonalityCreate = {
  enabled?: boolean;
  displayName: string;
  avatar: AgenticPersonalityAvatar;
  tone: ToneProfile;
  voice: AgenticPersonalityVoice;
};

/** Writable personality fields accepted by PATCH (create or update). */
export type AgenticPersonalityPatch = {
  personalityId?: string;
  enabled?: boolean;
  displayName?: string;
  avatar?: AgenticPersonalityAvatar;
  tone?: ToneProfile;
  voice?: AgenticPersonalityVoice;
};

export type LauncherLeadingIconGlyph = 'sparkle' | 'star';

export type AgentSelectorMobileSettings = {
  backgroundColor: string;
  labelText: string;
  labelTextColor: string;
  showLeadingIcon: boolean;
  leadingIcon: LauncherLeadingIconGlyph;
  leadingIconColor: string;
};

export type AgentSelectorSettings = {
  backgroundColor: string;
  showLeadingIcon: boolean;
  leadingIcon: LauncherLeadingIconGlyph;
  leadingIconColor: string;
  mobile: AgentSelectorMobileSettings;
};

export type AgenticAgentSettings = {
  enabled?: boolean;
  promptManagement: AgenticPromptManagement;
  personalities?: AgenticPersonality[];
  voice?: {
    pronunciationGlossary: AgenticPronunciationEntry[];
  };
  productExperience: {
    productDisplay: ProductDisplay;
    widgetPosition?: { right: number; bottom: number };
    agentSelector?: AgentSelectorSettings;
    roomScenes?: AgenticRoomScenes;
  };
  engagement: {
    intrusiveness: number;
    upsellPosture: UpsellPosture;
    confidencePosture: ConfidencePosture;
    proactiveness: ProactivenessLevel;
  };
  escalation: AgenticEscalationSettings;
  checkoutMode: {
    mode: CheckoutMode;
    handoffUrl?: string;
    orderCompleteUrl?: string;
  };
  rules?: EllaRule[];
  support?: AgenticSupportSettings;
  knowledgeBase?: AgenticKnowledgeBaseSettings;
  isInherited?: boolean;
};

export type StoreReadinessStatus = 'pending' | 'complete';

export type StoreReadiness = {
  status: StoreReadinessStatus;
  /** ISO-8601 timestamp of the last successful product catalog sync. Null until synced. */
  lastCatalogSyncAt: string | null;
};

/** Partial update for PATCH /merchants/{merchantId}/agentic-settings storeReadiness. */
export type StoreReadinessPatch = {
  status?: StoreReadinessStatus;
  lastCatalogSyncAt?: string | null;
};

export type PatchStoreReadinessArgs = {
  merchantId: string;
  body: {
    storeReadiness: StoreReadinessPatch;
  };
};

export type MerchantAgenticSettings = {
  agenticSettingsId: string;
  merchantId: string;
  /** Document-level store onboarding readiness for agentic commerce. */
  storeReadiness?: StoreReadiness;
  agents: {
    /** Storefront gate: public GET includes agenticSettings only when true. */
    enabled?: boolean;
  } & {
    [K in AgentName]?: AgenticAgentSettings;
  };
  isDeleted?: boolean;
};

/** Writable prompt-management fields accepted by PATCH (name and tone live on personalities). */
export type AgenticPromptManagementPatch = {
  formality?: FormalityLevel;
  sentenceStyle?: SentenceStyle;
  customGreeting?: string;
  brandVocabulary?: {
    mirrorSiteTerminology: boolean;
  };
  suggestedPrompts?: SuggestedPromptLabel[];
};

/** Writable engagement fields accepted by PATCH. */
export type AgenticEngagementPatch = {
  intrusiveness?: number;
  upsellPosture?: UpsellPosture;
  confidencePosture?: ConfidencePosture;
  proactiveness?: ProactivenessLevel;
};

/** Body for PATCH /merchants/{merchantId}/agentic-settings/{agentName}. */
export type AgenticAgentSettingsPatch = {
  promptManagement?: AgenticPromptManagementPatch;
  voice?: {
    pronunciationGlossary?: AgenticPronunciationEntry[];
  };
  productExperience?: {
    productDisplay?: ProductDisplay;
    /** Pass `null` to clear a stored widget position. */
    widgetPosition?: { right: number; bottom: number } | null;
    agentSelector?: Partial<Omit<AgentSelectorSettings, 'mobile'>> & {
      mobile?: Partial<AgentSelectorMobileSettings>;
    };
    roomScenes?: AgenticRoomScenes;
  };
  escalation?: AgenticEscalationSettings;
  checkoutMode?: {
    mode: CheckoutMode;
    /** Pass `null` to clear a stored handoff URL. */
    handoffUrl?: string | null;
    /** Pass `null` to clear a stored order-complete URL. */
    orderCompleteUrl?: string | null;
  };
  engagement?: AgenticEngagementPatch;
  rules?: EllaRule[];
  support?: AgenticSupportSettings;
  knowledgeBase?: AgenticKnowledgeBaseSettings;
  /** New or updated agent identities. Omitted when personalities are unchanged. */
  personalities?: AgenticPersonalityPatch[];
};

export type PatchAgenticSettingsArgs = {
  merchantId: string;
  agentName: AgentName;
  body: AgenticAgentSettingsPatch;
};

/** Body for POST /merchants/{merchantId}/agentic-settings/{agentName}/voice-preview. */
export type AgenticVoicePreviewBody = {
  voiceProfileId: string;
  text: string;
};

export type PreviewAgenticVoiceArgs = {
  merchantId: string;
  agentName: AgentName;
  body: AgenticVoicePreviewBody;
};

/** Response for GET /merchants/{merchantId}/agentic-settings/voice-profiles. */
export type AgenticVoiceProfilesResponse = {
  voiceProfileIds: string[];
};

export type DeleteAgenticPersonalityArgs = {
  merchantId: string;
  agentName: AgentName;
  personalityId: string;
};

/** Response for POST /merchants/{merchantId}/agentic-settings/avatars. */
export type AgenticAvatarUploadResponse = {
  avatarSrc?: string;
  src?: string;
  avatar?: AgenticPersonalityAvatar;
};

export type UploadAgenticAvatarArgs = {
  merchantId: string;
  file: File;
};

/** Writable agent settings accepted by POST /merchants/{merchantId}/agentic-settings. */
export type AgenticRoomSceneImage = {
  src: string;
  /** Which part of the image stays in view when it is cropped, as percentages. */
  focalPoint?: { x: number; y: number };
};

export type AgenticRoomScene = {
  id: string;
  name: string;
  /** Tells the agent when this scene fits. */
  whenToUse: string;
  image: AgenticRoomSceneImage;
  /** Cropped for a phone held upright; falls back to `image`. */
  imagePhone?: AgenticRoomSceneImage;
};

/** Backdrops for the full-page view. Replaced whole on PATCH. */
export type AgenticRoomScenes = {
  enabled: boolean;
  defaultSceneId?: string;
  scenes: AgenticRoomScene[];
};

export type RoomSceneImageUploadResponse = {
  imageSrc: string;
};

export type AgenticAgentSettingsCreate = {
  promptManagement: {
    formality: FormalityLevel;
    sentenceStyle: SentenceStyle;
    customGreeting: string;
    brandVocabulary: {
      mirrorSiteTerminology: boolean;
    };
    suggestedPrompts: SuggestedPromptLabel[];
  };
  voice?: {
    pronunciationGlossary: AgenticPronunciationEntry[];
  };
  productExperience: {
    productDisplay: ProductDisplay;
    widgetPosition?: { right: number; bottom: number };
    agentSelector?: AgentSelectorSettings;
    roomScenes?: AgenticRoomScenes;
  };
  engagement: {
    intrusiveness: number;
    upsellPosture: UpsellPosture;
    confidencePosture: ConfidencePosture;
    proactiveness: ProactivenessLevel;
  };
  escalation?: AgenticEscalationSettings;
  checkoutMode: {
    mode: CheckoutMode;
    handoffUrl?: string;
    orderCompleteUrl?: string;
  };
  rules?: EllaRule[];
  support?: AgenticSupportSettings;
  knowledgeBase?: AgenticKnowledgeBaseSettings;
  /** New agent identities. Omit `personalityId`; the server assigns ids. */
  personalities?: AgenticPersonalityCreate[];
};

export type CreateAgenticSettingsBody = {
  agents: {
    [K in AgentName]?: AgenticAgentSettingsCreate;
  };
};

export type PostAgenticSettingsArgs = {
  merchantId: string;
  body: CreateAgenticSettingsBody;
};

/** Agent metrics (MMS `GET /merchants/{id}/agents/{agentName}/metrics`). */
export type PromptDriftItem = {
  promptVersion: string;
  sessionCount: number;
  driftScore: number | null;
};

export type IntentTagItem = {
  tag: string;
  count: number;
  percentage: number;
};

export type ToolUsageItem = {
  toolName: string;
  callCount: number;
  errorRate: number;
};

export type DailyTrendItem = {
  date: string;
  sessions: number;
  errorRate: number;
  taskCompletionRate: number;
  checkoutConversionRate: number | null;
};

export type AgentMetricsAnalysis = {
  promptDrift: { items: PromptDriftItem[] };
  intentTags: { items: IntentTagItem[] };
  toolUsage: { items: ToolUsageItem[] };
  dailyTrend: { items: DailyTrendItem[] };
};

/** Median shopper wait for Ella's first words, plus how many turns it covers. */
export type ResponseLatency = {
  p50Ms: number | null;
  measuredResponses: number;
  eligibleResponses: number;
};

export type AgentMilestones = {
  sessionReached: number;
  productReached: number;
  cartReached: number;
  checkoutReached: number;
};

export type AgentMetricsResponse = {
  sessions: number;
  avgSessionDurationSec: number | null;
  responseLatency: ResponseLatency;
  toolAccuracyRate: number | null;
  reliabilityRate: number | null;
  groundedResponseRate: number;
  taskCompletionRate: number;
  checkoutConversionRate: number | null;
  agentMilestones: AgentMilestones;
  repeatUsageRate: number | null;
  escalationRate: number;
  timeToCheckoutSec: number | null;
  errorRate: number;
  toolErrorRate: number;
  avgToolCallsPerSession: number;
  avgLlmCallsPerSession: number;
  avgTokensPerSession: number;
  estimatedCostUsd: number;
  analysis: AgentMetricsAnalysis;
};

export type AgentWidgetClick = {
  personalityId: string;
  clicks: number;
};

export type AgentWidgetInputCounts = {
  typed: number;
  spoken: number;
  unknown: number;
};

export type AgentWidgetMicResults = {
  granted: number;
  denied: number;
  failed: number;
};

/** MMS `GET /merchants/{id}/metrics/agent-widget`. Daily distinct browsers, summed. No personality filter. */
export type AgentWidgetMetricsResponse = {
  hasEvents: boolean;
  saw: number;
  opened: number;
  used: number;
  ignored: number;
  clicks: AgentWidgetClick[];
  input?: AgentWidgetInputCounts;
  mic: AgentWidgetMicResults;
};

export type GetAgentWidgetMetricsArgs = {
  merchantId: string;
  from?: string;
  to?: string;
  excludeTestSessions?: boolean;
};

export type OrderAttributionClassTotals = {
  revenue: string;
  orders: number;
};

export type OrderAttributionCurrency = {
  currency: string;
  direct: OrderAttributionClassTotals;
  attributed: OrderAttributionClassTotals;
  assisted: OrderAttributionClassTotals;
  conciergeRevenue: string;
  conciergeOrders: number;
  averageOrderValue: string | null;
};

/** MMS `GET /merchants/{id}/metrics/order-attribution`. Totals for the range. Test orders stay in. */
export type OrderAttributionMetricsResponse = {
  hasData: boolean;
  currencies: OrderAttributionCurrency[];
};

export type GetOrderAttributionMetricsArgs = {
  merchantId: string;
  from?: string;
  to?: string;
  personalityIds?: string[];
};

/** MMS `GET /merchants/{id}/agents/{agentName}/metrics`. Omit `personalityIds` for all traces. */
export type GetAgentMetricsArgs = {
  merchantId: string;
  agentName: string;
  from?: string;
  to?: string;
  excludeTestSessions?: boolean;
  /** One or more personality UUIDs (`?personalityIds=id` or comma-separated). */
  personalityIds?: string[];
};

/** FLEX_PAY merchant config value */
export type FlexPayVertical =
  'retail' | 'air' | 'hotel' | 'cruise' | 'event_tickets' | 'rentals' | 'passes_subscription';

export type FlexPayConfigValue = {
  upCode?: string;
  useSkipifyUpCode: boolean;
  vertical: FlexPayVertical;
};

export type NonAcceptedCardBrandsValue = {
  nonAcceptedCardBrands: CardBrand[];
};

export type TippingPercentagesValue = number[];

export type AddConfigBody = {
  merchantId: string;
  name: string;
  value: JSONValue;
  type?: string;
  enabled?: boolean;
};

export type DomainResponse = {
  urlId: string;
  url: string;
  merchantId: string;
  backendUrl: string;
  isDeleted: boolean;
};

export const callbackTypes = [
  'Products',
  'ShippingAndTax',
  'Fulfillability',
  'ShopperCredentialSharing',
  'AgenticCheckoutValidateTotal',
] as const;
export type CallbackType = (typeof callbackTypes)[number];

// A simplified type for payment callbacks with URL and headers
export type PaymentCallback = {
  url: string;
  headerKey?: string;
  headerValue?: string;
};

export type CallbackResponse = {
  callbackId: string;
  merchantId: string;
  callbackType: CallbackType;
  url: string;
  authorizationToken?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  headers?: Record<string, string>;
  isInherited?: boolean;
};

export type MerchantAccess = {
  merchantId: string;
  name: string;
  partnerMerchantId?: string | null;
};

export type Country = {
  countryCode: string;
  countryName: string;
  countryId: string;
  countryPhoneCode: string;
};

export type ApiKeyResponse = {
  apiKeyId: string;
  hash: string;
  merchantId: string;
  createdAt: string;
  updatedAt: string;
  isInherited?: boolean;
};

export type CreateApiKeyResponse = ApiKeyResponse & {
  apiKey: string;
};

export const shopifyOnboardingStepKeys = [
  'app_init',
  'distribution',
  'app_deploy',
  'credentials_saved',
  'oauth_complete',
  'embed_enabled',
] as const;
export type ShopifyOnboardingStepKey = (typeof shopifyOnboardingStepKeys)[number];
export type OnboardingStepStatus = 'pending' | 'in_progress' | 'done' | 'blocked' | 'failed';

export type ShopifyOnboardingStep = {
  key: ShopifyOnboardingStepKey;
  status: OnboardingStepStatus;
  actor?: string;
  updatedAt?: string;
  meta?: Record<string, unknown>;
};

export const bigcommerceOnboardingStepKeys = [
  'store_registered',
  'oauth_complete',
  'scripts_injected',
  'setup_verified',
] as const;
export type BigCommerceOnboardingStepKey = (typeof bigcommerceOnboardingStepKeys)[number];

export type BigCommerceOnboardingStep = {
  key: BigCommerceOnboardingStepKey;
  status: OnboardingStepStatus;
  actor?: string;
  updatedAt?: string;
  meta?: Record<string, unknown>;
};

export type OnboardingSystemOfOrigin = 'shopify' | 'bigcommerce';

export type MerchantOnboardingBase = {
  onboardingId: string;
  merchantId: string;
  externalPlatformId: string;
  lastRunId?: string | null;
  merchantName?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type ShopifyMerchantOnboarding = MerchantOnboardingBase & {
  systemOfOrigin: 'shopify';
  steps: ShopifyOnboardingStep[];
};

export type BigCommerceMerchantOnboarding = MerchantOnboardingBase & {
  systemOfOrigin: 'bigcommerce';
  steps: BigCommerceOnboardingStep[];
};

export type MerchantOnboarding = ShopifyMerchantOnboarding | BigCommerceMerchantOnboarding;

export type CreateShopifyOnboardingBody = {
  systemOfOrigin: 'shopify';
  externalPlatformId: string;
};

export type CreateBigCommerceOnboardingBody = {
  systemOfOrigin: 'bigcommerce';
  externalPlatformId: string;
};

export type CreateOnboardingBody = CreateShopifyOnboardingBody | CreateBigCommerceOnboardingBody;

export type PatchOnboardingFieldsBody = {
  lastRunId?: string | null;
  externalPlatformId?: string;
};

export type PatchOnboardingStepBody = {
  status: OnboardingStepStatus;
  meta?: Record<string, unknown>;
  lastRunId?: string | null;
};

export type ShopifyCredentialConfiguration = {
  shopUrl: string;
  token: string;
  apiKey: string;
  apiSecretKey: string;
  clientId?: string;
  clientSecret?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
  /** App Automation Token, used to deploy this merchant's Partner app. Shopify shows it once. */
  appAutomationToken?: string;
  appAutomationTokenExpiresAt?: string;
  [key: string]: unknown;
};

export const externalCredentialTypes = [
  'payment',
  'ecommerce',
  'email',
  'billing',
  'checkout',
  'bank',
  'support',
] as const;
export type ExternalCredentialType = (typeof externalCredentialTypes)[number];

export const paymentGatewayTypes = [
  'Adyen',
  'Fiserv',
  'Payrix',
  'Braintree',
  'Stripe',
  'Express',
  'RAFT',
  'VAP',
  'WPG',
  'Maverick',
] as const;
export type PaymentGatewayType = (typeof paymentGatewayTypes)[number];

/** Lowercase PSP / gateway names so values like "Raft" and "RAFT" compare equal. */
export const normalizeGateway = (gateway?: string | null) => gateway?.toLowerCase();

export const findPaymentGatewayType = (origin?: string | null): PaymentGatewayType | undefined =>
  paymentGatewayTypes.find((gateway) => normalizeGateway(gateway) === normalizeGateway(origin));

export type BillingCredentialData = {
  billingMid: string;
  billingChainId: string;
};

export type StripeCredentialData = {
  connect_id: string;
  api_key?: string;
};

export type BraintreeCredentialData = {
  public_key: string;
  private_key: string;
  merchant_account_id?: string;
};

export type AdyenCredentialData = {
  api_key: string;
  merchant_account: string;
};

export type FiservCredentialData = {
  api_key: string;
  secret: string;
  terminal_id: string;
  merchant_id: string;
};

export type PayrixCredentialData = {
  api_key: string;
  merchant_id: string;
};

export type MaverickCredentialData = {
  access_token: string;
  terminal_id: string;
};

export type ExpressCredentialData = {
  acceptor_id: string;
  account_id: string;
  account_token: string;
  terminal_id?: string;
};

export type RaftCredentialData = {
  bank_terminal_id?: string; // Tied to UseUpdatedRAFT flag - make required once flag no longer in use (OTC 21/12/22)
  bank_id?: string; // Tied to UseUpdatedRAFT flag - make required once flag no longer in use (OTC 21/12/22)
  merchant_id: string;
};

export type VapCredentialData = {
  username: string;
  password: string;
  merchant_id: string;
};

export type WpgCredentialData = {
  user_name: string;
  password: string;
  merchant_code: string;
};

export type SupportSystemOfOrigin = 'gorgias';
/** Helpdesk connection stored on the `support` external credential (MMS support-connector). */
export type SupportCredentialConfiguration = {
  domain: string;
  email: string;
  apiKey: string;
};
export type SupportCredentialData = {
  configuration: SupportCredentialConfiguration;
};

export type BankSystemOfOrigin = 'aeropay';
export type BankCredentialData = {
  merchant_id: string;
};
export const DEFAULT_BANK_SYSTEM_OF_ORIGIN: BankSystemOfOrigin = 'aeropay';

export type PaymentCredentialData =
  | AdyenCredentialData
  | FiservCredentialData
  | PayrixCredentialData
  | StripeCredentialData
  | BraintreeCredentialData
  | ExpressCredentialData
  | RaftCredentialData
  | VapCredentialData
  | WpgCredentialData
  | MaverickCredentialData;

export type MerchantExternalCredential<SystemOfOrigin = string, Data = Record<string, unknown>> = {
  merchantId: string;
  credentialType: ExternalCredentialType;
  systemOfOrigin: SystemOfOrigin;
  data: Data;
  credentialId: string;
};

type CredentialAndConfigs = {
  credentials: MerchantExternalCredential[];
  configs: ConfigResponse[];
  default: string | null;
};

export type GetCredentialsResponse = {
  merchantId: string;
  payment?: CredentialAndConfigs;
  ecommerce?: CredentialAndConfigs;
  email?: CredentialAndConfigs;
  billing?: CredentialAndConfigs;
  checkout?: CredentialAndConfigs;
  bank?: CredentialAndConfigs;
  support?: CredentialAndConfigs;
};

export type GetMerchantResponse = {
  merchantId: string;
  name: string;
  externalCredentials: Partial<Record<ExternalCredentialType, string>>;
  apiKey?: string;
  directLineage?: string[];
  partnerMerchantId?: string | null;
  externalPlatformId?: string | null;
  hasDescendants?: boolean;
};

export type UpdateDefaultCredentialsBody = Partial<Record<ExternalCredentialType, string | null>>;

/** Replaces the default ecommerce credential and sets `externalPlatformId` to `externalId`. */
export type UpdateMerchantEcommerceConnector = {
  type: string;
  externalId: string;
  apiVersion: string;
  configuration: Record<string, unknown>;
};

export type UpdateMerchantBody = {
  ecommerceConnector?: UpdateMerchantEcommerceConnector;
};

export type AcceptedCurrency = {
  code: string;
  symbol: string;
  decimalPlaces: number;
};

export type CreateChildMerchantBody = {
  name: string;
  partnerMerchantId: string;
  credentials?: {
    credentialType: ExternalCredentialType;
    systemOfOrigin: string;
    data: Record<string, unknown>;
  }[];
  configs?: Pick<ConfigResponse, 'name' | 'value' | 'type' | 'enabled'>[];
  urls?: Pick<DomainResponse, 'backendUrl' | 'url'>[];
};

export type CreateChildMerchantResponse = {
  merchantId: string;
  name: string;
  partnerMerchantId: string;
  apiKeys: CreateApiKeyResponse[];
} & Record<string, unknown>;

export type CreateMerchantBody = {
  name: string;
  industry: string;
  supportEmail?: string;
  externalPlatformId?: string | null;
  credentials?: {
    credentialType: ExternalCredentialType;
    systemOfOrigin: string;
    data: Record<string, unknown>;
  }[];
  urls?: Pick<DomainResponse, 'backendUrl' | 'url'>[];
};

export type CreateMerchantResponse = {
  merchantId: string;
  name: string;
  apiKeys: CreateApiKeyResponse[];
} & Record<string, unknown>;

export type Auth0RoleSummary = {
  id: string;
  name: string;
  description: string;
};

export type Auth0PermissionSummary = {
  name: string;
  description?: string;
};

export type Auth0RoleWithPermissions = Auth0RoleSummary & {
  permissions: Auth0PermissionSummary[];
};

export type PortalUserListItem = {
  userId: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  givenName?: string;
  familyName?: string;
  createdAt?: string;
  lastLogin?: string;
  hasLoggedIn: boolean;
  roles: Auth0RoleSummary[];
  merchantCount: number;
  /** Set when searching with grantMerchantId (grant-access flow). */
  hasDirectAccessToMerchant?: boolean;
};

export type PortalUserSortBy = 'givenName' | 'familyName' | 'email';

export type PortalUserSortQuery = {
  sortBy?: PortalUserSortBy;
  orderBy?: 'asc' | 'desc';
};

export type PortalUserDetail = PortalUserListItem & {
  merchants: MerchantAccess[];
};

export type SearchPortalUsersQuery = PortalUserSortQuery & {
  searchQuery?: string;
  roleNames?: string[];
  merchantIds?: string[];
  grantMerchantId?: string;
  pageNumber?: number;
  pageSize?: number;
};

export type MerchantPortalUsersQuery = PortalUserSortQuery & {
  merchantId: string;
  searchQuery?: string;
  pageNumber?: number;
  pageSize?: number;
};

export type CreatePortalUserBody = {
  email: string;
  firstName: string;
  lastName: string;
  roleNames: string[];
  merchantIds?: string[];
  sendAccessEmail?: boolean;
};

export type UpdatePortalUserBody = {
  firstName?: string;
  lastName?: string;
  roleNames?: string[];
  merchantIds?: string[];
  sendAccessEmail?: boolean;
};

export const portalUserAuditLogActions = ['create', 'update', 'delete', 'sendAccessEmail'] as const;
export type PortalUserAuditLogAction = (typeof portalUserAuditLogActions)[number];

export type PortalUserAuditSnapshot = {
  email?: string;
  givenName?: string | null;
  familyName?: string | null;
  roleNames: string[];
  merchantIds: string[];
};

export type PortalUserAuditChange = {
  field: 'givenName' | 'familyName' | 'roleNames' | 'merchantIds';
  from: unknown;
  to: unknown;
};

export type PortalUserAuditLogEntry = {
  auditLogId: string;
  targetUserId: string;
  targetUserEmail: string;
  givenName?: string | null;
  familyName?: string | null;
  action: PortalUserAuditLogAction;
  actorUserId: string;
  actorEmail: string;
  timestamp: string;
  previousValue: PortalUserAuditSnapshot | null;
  newValue: PortalUserAuditSnapshot | null;
  changes: PortalUserAuditChange[];
  metadata: { emailType?: 'activation' | 'passwordReset' } | null;
};

export const portalUserAuditEmailTypes = ['activation', 'passwordReset'] as const;
export type PortalUserAuditEmailType = (typeof portalUserAuditEmailTypes)[number];

/** Filter checkbox options — email subtypes map to action=sendAccessEmail + emailType. */
export const portalUserAuditLogFilterOptions = [
  'create',
  'update',
  'delete',
  'activation',
  'passwordReset',
] as const;
export type PortalUserAuditLogFilterOption = (typeof portalUserAuditLogFilterOptions)[number];

export type GetPortalUserAuditLogsQuery = {
  targetUserId?: string;
  searchQuery?: string;
  actorEmail?: string;
  action?: PortalUserAuditLogAction[];
  emailType?: PortalUserAuditEmailType[];
  createdBefore?: string;
  createdAfter?: string;
  pageSize?: number;
  pageNumber?: number;
  orderBy?: OrderByValue;
};
